import { Component, OnInit, OnDestroy, Inject, NgZone } from '@angular/core';
import { StepComponent } from '@icc/configurator/shared';
import { _, isObject } from '@icc/shared/helpers';
import { WindowFacade } from '../+state/window-door.facade';
import { SubscriptionLike } from 'rxjs';
import { ConfigurationsService } from '@icc/configurator/shared';
import { FillingsFacade } from '../glazings/fillings.facade';
import { APP_CONFIG, AppConfigFactory, TranslateService } from '@icc/shared';
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { ProfilesService } from '@icc/construction-elements/configurator';
import { LippingColorsService } from '../profiles/lipping-colors.service';
import { ColorDto as IccColor } from '@icc/construction-elements/types';
import { ActiveSash } from '@icc/window-door/types';
import { ColoredElements, ColoredSashes, ColoredSides, ProfileColoredSides } from '@icc/construction-elements/configurator';
import { NewColorsService } from '@icc/construction-elements/configurator';
import { ColorControl, ColorControlDef } from '@icc/construction-elements/configurator';
import { ProfileColorsService } from '@icc/construction-elements/configurator';

@Component({
    selector: 'icc-colors',
    templateUrl: './colors.component.html',
    styleUrls: ['./colors.component.scss'],
    standalone: false
})
export class ColorsComponent extends StepComponent implements OnInit, OnDestroy {
    static stepName = _('WINDOW|Kolor');
    static stepIcon = {
        ligature: 'palette',
    };

    public configurator = 'window';
    public stepId = 'colors';
    public title = _('WINDOW|Wybierz kolor:');
    public options = [
        {
            title: _('WINDOW|Kolory podstawowe'),
            callback: () => {
                this.mode = 'basic';
                this.newColorsService.setExtendedMode(false);
            },
            icon: {
                ligature: 'invert_colors',
            },
            show: () => this.mode === 'extended',
            hover: 'active',
        },
        {
            title: _('WINDOW|Kolory zaawansowane'),
            callback: () => {
                this.mode = 'extended';
                this.newColorsService.setExtendedMode(true);
            },
            icon: {
                ligature: 'invert_colors',
            },
            show: () => this.mode === 'basic' && this.availableColorsSashExt(),
        },
    ];

    onlyDoubleSidedFrameColor = false;
    differentDoorColors = false;

    controls: ColorControl[] = [];
    controlsDef: Record<
        ColorsComponent['mode'],
        Record<'window' | 'profileDoor' | 'panelDoor', ColorControlDef[]>
    > = {
        basic: {
            window: [
                {
                    title: _('WINDOW|Kolor zewnętrzny panelu'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                },
                {
                    title: _('WINDOW|Kolor wewnętrzny panelu'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                },
                {
                    title: _('WINDOW|Kolor zewnętrzny'),
                    element: 'sash',
                    side: 'outer',
                },
                {
                    title: _('WINDOW|Kolor nakładki aluminiowej'),
                    element: 'sash',
                    side: 'alushell',
                },
                {
                    title: _('WINDOW|Kolor wewnętrzny'),
                    element: 'sash',
                    side: 'inner',
                    whiteSwitch: true,
                },
                {
                    title: _('WINDOW|Kolor bazy'),
                    element: 'sash',
                    side: 'core',
                },
                {
                    title: _('WINDOW|Kolor progu'),
                    element: 'threshold',
                },
            ],
            profileDoor: [
                {
                    title: _('DOOR|Kolor zewnętrzny skrzydła aktywnego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny skrzydła aktywnego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'activeDoorSash',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji skrzydła aktywnego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji skrzydła aktywnego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'activeDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny skrzydła biernego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'passiveDoorSash',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji skrzydła biernego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'passiveDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny panelu doświetla'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji panelu doświetla'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny'),
                    element: 'sash',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor nakładki aluminiowej'),
                    element: 'sash',
                    side: 'alushell',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny'),
                    element: 'sash',
                    side: 'inner',
                    whiteSwitch: true,
                },
                {
                    title: _('DOOR|Kolor bazy'),
                    element: 'sash',
                    side: 'core',
                },
                {
                    title: () =>
                        this.onlyDoubleSidedFrameColor
                            ? _('DOOR|Kolor ościeżnicy')
                            : _('DOOR|Kolor zewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'inner',
                    hide: () => this.onlyDoubleSidedFrameColor,
                },
                {
                    title: _('DOOR|Kolor progu'),
                    element: 'threshold',
                },
            ],
            panelDoor: [
                {
                    title: _('DOOR|Kolor drzwi'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                },
                {
                    title: _('DOOR|Kolor aplikacji drzwi'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                },
                {
                    title: _('DOOR|Kolor aplikacji skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                },
                {
                    title: _('DOOR|Kolor aplikacji panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                    alt: true,
                },
                {
                    title: () =>
                        !this.hasFrameInnerColorSelect()
                            ? _('DOOR|Kolor ościeżnicy')
                            : _('DOOR|Kolor zewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'inner',
                },
                {
                    title: _('DOOR|Kolor progu'),
                    element: 'threshold',
                },
                {
                    title: () =>
                        !this.hasLippingInnerColor()
                            ? _('DOOR|Kolor listwy mocującej')
                            : _('DOOR|Kolor zewnętrzny listwy mocującej'),
                    element: 'lipping',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny listwy mocującej'),
                    element: 'lipping',
                    side: 'inner',
                },
                {
                    title: _('DOOR|Kolor portalu'),
                    element: 'doorPortal',
                },
                {
                    title: _('DOOR|Kolor lameli'),
                    element: 'slat',
                },
            ],
        },
        extended: {
            window: [
                {
                    title: _('WINDOW|Kolor zewnętrzny panelu'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                },
                {
                    title: _('WINDOW|Kolor wewnętrzny panelu'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                },
                {
                    title: _('WINDOW|Kolor zewnętrzny skrzydła'),
                    element: 'sash',
                    side: 'outer',
                },
                {
                    title: _('WINDOW|Kolor nakładki aluminiowej skrzydła'),
                    element: 'sash',
                    side: 'alushell',
                },
                {
                    title: _('WINDOW|Kolor wewnętrzny skrzydła'),
                    element: 'sash',
                    side: 'inner',
                    whiteSwitch: true,
                },
                {
                    title: _('WINDOW|Kolor bazy skrzydła'),
                    element: 'sash',
                    side: 'core',
                },
                {
                    title: _('WINDOW|Kolor zewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'outer',
                },
                {
                    title: _('WINDOW|Kolor nakładki aluminiowej ościeżnicy'),
                    element: 'frame',
                    side: 'alushell',
                },
                {
                    title: _('WINDOW|Kolor wewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'inner',
                    whiteSwitch: true,
                },
                {
                    title: _('WINDOW|Kolor bazy ościeżnicy'),
                    element: 'frame',
                    side: 'core',
                },
                {
                    title: _('WINDOW|Kolor progu'),
                    element: 'threshold',
                },
            ],
            profileDoor: [
                {
                    title: _('DOOR|Kolor zewnętrzny skrzydła aktywnego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny skrzydła aktywnego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'activeDoorSash',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji skrzydła aktywnego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji skrzydła aktywnego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'activeDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny skrzydła biernego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'passiveDoorSash',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji skrzydła biernego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'passiveDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny panelu doświetla'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji panelu doświetla'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny profilu skrzydła'),
                    element: 'sash',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor nakładki aluminiowej profilu skrzydła'),
                    element: 'sash',
                    side: 'alushell',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny profilu skrzydła'),
                    element: 'sash',
                    side: 'inner',
                    whiteSwitch: true,
                },
                {
                    title: _('DOOR|Kolor bazy profilu skrzydła'),
                    element: 'sash',
                    side: 'core',
                },
                {
                    title: () =>
                        this.onlyDoubleSidedFrameColor
                            ? _('DOOR|Kolor ościeżnicy')
                            : _('DOOR|Kolor zewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'inner',
                },
                {
                    title: _('DOOR|Kolor progu'),
                    element: 'threshold',
                },
            ],
            panelDoor: [
                {
                    title: _('DOOR|Kolor zewnętrzny drzwi'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny drzwi'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'activeDoorSash',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji drzwi'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'activeDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji drzwi'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'activeDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny skrzydła biernego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'passiveDoorSash',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji skrzydła biernego'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'passiveDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji skrzydła biernego'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'passiveDoorSash',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor zewnętrzny panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny panelu doświetla'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                },
                {
                    title: _('DOOR|Kolor zewnętrzny aplikacji panelu doświetla'),
                    element: 'filling',
                    side: 'outer',
                    sash: 'window',
                    alt: true,
                },
                {
                    title: _('DOOR|Kolor wewnętrzny aplikacji panelu doświetla'),
                    element: 'filling',
                    side: 'inner',
                    sash: 'window',
                    alt: true,
                },
                {
                    title: () =>
                        this.onlyDoubleSidedFrameColor
                            ? _('DOOR|Kolor ościeżnicy')
                            : _('DOOR|Kolor zewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny ościeżnicy'),
                    element: 'frame',
                    side: 'inner',
                },
                {
                    title: _('DOOR|Kolor progu'),
                    element: 'threshold',
                },
                {
                    title: () =>
                        !this.hasLippingInnerColor()
                            ? _('DOOR|Kolor listwy mocującej')
                            : _('DOOR|Kolor zewnętrzny listwy mocującej'),
                    element: 'lipping',
                    side: 'outer',
                },
                {
                    title: _('DOOR|Kolor wewnętrzny listwy mocującej'),
                    element: 'lipping',
                    side: 'inner',
                },
                {
                    title: _('DOOR|Kolor portalu'),
                    element: 'doorPortal',
                },
                {
                    title: _('DOOR|Kolor lameli'),
                    element: 'slat',
                },
            ],
        },
    };

    mode: 'basic' | 'extended' = 'basic';
    hiddenSelectionsWithoutChange = false;

    private subscriptions: SubscriptionLike[] = [];

    // eslint-disable-next-line max-params
    constructor(
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private windowFacade: WindowFacade,
        private fillingsFacade: FillingsFacade,
        private newColorsService: NewColorsService,
        private configurationsService: ConfigurationsService<'window'>,
        private translateService: TranslateService,
        private lippingColorsService: LippingColorsService,
        private profilesService: ProfilesService,
        private profileColorsService: ProfileColorsService,
        private ngZone: NgZone,
    ) {
        super();
    }

    ngOnInit() {
        this.hiddenSelectionsWithoutChange = this.configurationsService.conf.Current?.type === 'door' && this.config().IccConfig.Configurators.door.hideSelectionsWithoutChange;
        this.subscriptions.push(
            this.windowFacade.colorSashExt$.subscribe((colorSashExt) => {
                if (colorSashExt && this.mode !== 'extended' && this.availableColorsSashExt()) {
                    this.mode = 'extended';
                } else if (!colorSashExt && this.mode === 'extended') {
                    this.mode = 'basic';
                }
            })
        );

        this.subscriptions.push(
            this.fillingsFacade.hasDifferentColors$.subscribe((differentColors) => {
                this.differentDoorColors =
                    Boolean(differentColors) &&
                    this.configurationsService.conf.Current.type === 'door' &&
                    this.configurationsService.conf.Current.System.door_type;
                if (this.differentDoorColors) {
                    this.mode = 'extended';
                }
            })
        );

        this.subscriptions.push(
            this.configurationsService.configuration$.subscribe((conf) => {
                this.onlyDoubleSidedFrameColor = this.newColorsService.onlyDoubleSidedFrameColor(
                    conf
                );
                this.updateControls(conf);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    updateControls(conf = this.configurationsService.conf.Current) {
        const confType = this.getConfType(conf);
        const controls: ColorControl[] = [];
        for (const controlDef of this.controlsDef[this.mode][confType]) {
            if (this.shouldSkipControl(conf, controlDef)) {
                continue;
            }

            const colorValue = this.newColorsService.getElementColor(conf, controlDef);
            const availableColors = this.newColorsService.getMatchingColorsFor(conf, controlDef);
            const switchVisibility = this.getSwitchVisibility(availableColors, controlDef, conf);
            if(controlDef.side === 'core' && !availableColors.length || controlDef.element === 'threshold' && availableColors.length <= 1 && this.hiddenSelectionsWithoutChange) {
                continue;
            }
            const control = this.createControl(controlDef, switchVisibility, colorValue);
            controls.push(control);
        }
        if (this.mode === 'extended' && this.config().IccConfig.Configurators.frameBeforeSashColor) {
            const frameControls = controls.filter(c => c.element === 'frame');
            const sashControls = controls.filter(c => c.element === 'sash');
            const restControls = controls.filter(c => c.element !== 'frame' && c.element !== 'sash');
            this.controls = [...frameControls, ...sashControls, ...restControls];
        } else {
            this.controls = controls;
        }
    }

    getConfType(conf = this.configurationsService.conf.Current) {
        let confType: 'window' | 'profileDoor' | 'panelDoor' =
            conf.type === 'door' ? 'profileDoor' : 'window';
        if (conf.System.door_type) {
            confType = 'panelDoor';
        }
        return confType;
    }

    shouldSkipControl(conf = this.configurationsService.conf.Current, controlDef: ColorControlDef) {
        if (controlDef.hide && controlDef.hide()) {
            return true;
        }
        if (controlDef.side === 'alushell' && (!conf.HasAlushell || conf.System?.type === 'alu')) {
            return true;
        }
        if (conf.HasAlushell && controlDef.side === 'outer') {
            return true;
        }

        if (conf.type === 'door' && controlDef.element === 'sash' && this.hasDecoFillingElement(conf) && this.mode === 'basic') {
            return true;
        }

        if (conf.type === 'door' && controlDef.element === 'frame' && !this.hasDecoFillingElement(conf) && this.mode === 'basic') {
            return true;
        }

        if (controlDef.element === 'frame' && !this.hasFrameColorSelect(conf)) {
            return true;
        }

        if (
            controlDef.element === 'frame' &&
            controlDef.side === 'inner' &&
            !this.hasFrameInnerColorSelect(conf)
        ) {
            return true;
        }

        if (controlDef.element === 'threshold' && !this.hasThresholdColorSelect(conf)) {
            return true;
        }
        if (controlDef.side === 'core' && !this.hasCoreColorSelect(conf, controlDef)) {
            return true;
        }
        if (controlDef.element === 'lipping' && !conf.System.available_lipping_color) {
            return true;
        }
        if (
            controlDef.element === 'lipping' &&
            controlDef.side === 'inner' &&
            !this.hasLippingInnerColor(conf)
        ) {
            return true;
        }
        if (controlDef.element === 'doorPortal' && !conf.doorPortal?.id) {
            return true;
        }

        if (controlDef.element === 'filling' && !this.hasFillingElement(conf)) {
            return true;
        }
        if (this.shouldSkipFilling(conf, controlDef)) {
            return true;
        }
        if (controlDef.element === 'slat' && !this.hasSlat(conf)) {
            return true;
        }

        if (controlDef.element === 'sash' && (!this.hasSash(conf) && this.mode === 'extended')) {
            return true;
        }

        return false;
    }

    shouldSkipFilling(conf: WindowActiveConfiguration, controlDef: ColorControlDef) {
        if (controlDef.element === 'filling') {
            if (controlDef.sash === 'activeDoorSash' && !this.hasDoorActiveSash(conf)) {
                return true;
            }

            if (controlDef.sash === 'passiveDoorSash' && !this.hasDoorPassiveSash(conf)) {
                return true;
            }

            if (controlDef.sash === 'window' && !this.hasWindowSashWithPanel(conf)) {
                return true;
            }

            const activeSash = isObject(controlDef.sash)
                ? controlDef.sash
                : this.newColorsService.getColoredSash(conf, controlDef.sash ?? 'activeDoorSash');

            if (!activeSash) {
                return true;
            }

            if (controlDef.side === 'inner' && !this.hasInnerFillingColor(conf, activeSash)) {
                return true;
            }

            if (
                controlDef.alt &&
                !this.hasAltFillingColor(conf, activeSash, controlDef.side ?? 'outer')
            ) {
                return true;
            }
        }
        return false;
    }

    getSwitchVisibility(
        availableColors: IccColor[],
        controlDef: ColorControlDef,
        conf: WindowActiveConfiguration
    ) {
        let isWhiteSwitchVisible = false;
        let isWhiteSwitchDisabled = true;
        let isBilateralSwitchVisible = false;
        let isMoreColorsSwitchVisible = false;
        const disabled = !availableColors?.length || availableColors?.length <= 1;
        if (
            controlDef.side === 'inner' &&
            (conf.System.type === 'pvc' || conf.System.type === 'wood') &&
            !conf.HasAlushell
        ) {
            const switchVisibility = this.isWhiteSwitchVisible(controlDef, conf, availableColors);
            isWhiteSwitchVisible = switchVisibility.whiteSwitchVisible ?? false;
            isBilateralSwitchVisible = switchVisibility.bilateralSwitchVisible ?? false;
            isMoreColorsSwitchVisible = !disabled;

            isWhiteSwitchDisabled = this.isWhiteColorButtonDisabled(
                controlDef.element,
                controlDef.side,
                controlDef.sash,
                controlDef.alt,
                conf
            );
        }
        return {
            isWhiteSwitchVisible,
            isBilateralSwitchVisible,
            isMoreColorsSwitchVisible,
            isWhiteSwitchDisabled,
            disabled,
        };
    }

    createControl(
        controlDef: ColorControlDef,
        switchVisibility: {
            isWhiteSwitchVisible: boolean;
            isBilateralSwitchVisible: boolean;
            isMoreColorsSwitchVisible: boolean;
            isWhiteSwitchDisabled: boolean;
            disabled: boolean;
        },
        colorValue: IccColor | null
    ) {
        return {
            title: typeof controlDef.title === 'function' ? controlDef.title() : controlDef.title,
            element: controlDef.element,
            side: controlDef.side,
            sash: controlDef.sash,
            profile: controlDef.profile,
            accessory: controlDef.accessory,
            alt: controlDef.alt,
            whiteSwitchVisible: switchVisibility.isWhiteSwitchVisible,
            bilateralSwitchVisible: switchVisibility.isBilateralSwitchVisible,
            moreColorsSwitchVisible: switchVisibility.isMoreColorsSwitchVisible,

            whiteSwitchDisabled: switchVisibility.isWhiteSwitchDisabled,
            disabled: switchVisibility.disabled,
            name: colorValue?.name || '',
            color: colorValue?.color || '',
            colorImg: '/files/color/' + (colorValue?.colorImg || ''),
        };
    }

    isWhiteSwitchVisible(
        colorControl: ColorControlDef,
        conf = this.configurationsService.conf.Current,
        availableColors: IccColor[] = []
    ) {
        if (colorControl.side !== 'inner') {
            return {
                whiteSwitchVisible: false,
                bilateralSwitchVisible: false,
            };
        }
        if (conf.System.type !== 'pvc' && conf.System.type !== 'wood') {
            return {
                whiteSwitchVisible: false,
                bilateralSwitchVisible: false,
            };
        }
        const outerColor = this.newColorsService.getElementColor(conf, {
            element: colorControl.element,
            side: 'outer',
            sash: colorControl.sash,
            alt: colorControl.alt,
        });
        const innerColor = this.newColorsService.getElementColor(conf, {
            element: colorControl.element,
            side: 'inner',
            sash: colorControl.sash,
            alt: colorControl.alt,
        });
        if (conf.System.type === 'wood') {
            return {
                whiteSwitchVisible: false,
                bilateralSwitchVisible:
                    outerColor?.id !== innerColor?.id &&
                    availableColors?.some((c) => c.id === outerColor?.id),
            };
        }

        const availableWhiteColor = this.newColorsService.getWhiteElementColor(conf, colorControl);

        if (!availableWhiteColor) {
            return {
                whiteSwitchVisible: false,
                bilateralSwitchVisible:
                    outerColor?.id !== innerColor?.id &&
                    availableColors?.some((c) => c.id === innerColor?.id),
            };
        }

        if (colorControl.element !== 'frame' && colorControl.element !== 'sash') {
            // Wyświetlamy przycisk jeśli:
            // 1. Kolor zewnętrzny jest inny niż wewnętrzny
            // 2. Kolor zewnętrzny jest biały - po to, żeby użytkownik był świadomy, że może zmienić kolor
            // 3. Kolor wewnętrzny nie jest biały - po to, żeby użytkownik mógł zmienić kolor
            return {
                whiteSwitchVisible:
                    (outerColor?.id === innerColor?.id &&
                        innerColor?.coreColors.every(
                            (c) => !innerColor.constructColors[c].some((el) => el.type === 'white')
                        )) ||
                    outerColor?.coreColors.some((c) =>
                        outerColor.constructColors[c].some((el) => el.type === 'white')
                    ),
                bilateralSwitchVisible:
                    (outerColor?.id !== innerColor?.id &&
                        (innerColor?.coreColors.some((c) =>
                            innerColor.constructColors[c].some((el) => el.type === 'white')
                        ) ||
                    outerColor?.coreColors.some((c) =>
                        outerColor.constructColors[c].some((el) => el.type === 'white')
                    )) &&
                        availableColors?.some((c) => c.id === outerColor?.id)),
            };
        } else {
            const outerConstrColor = this.newColorsService.getElementConstrColor(
                conf,
                colorControl.element,
                'outer'
            );
            const innerConstrColor = this.newColorsService.getElementConstrColor(
                conf,
                colorControl.element,
                'inner'
            );

            // Wyświetlamy przycisk jeśli:
            // 1. Kolor zewnętrzny jest inny niż wewnętrzny
            // 2. Kolor zewnętrzny jest biały - po to, żeby użytkownik był świadomy, że może zmienić kolor
            // 3. Kolor wewnętrzny nie jest biały - po to, żeby użytkownik mógł zmienić kolor
            return {
                whiteSwitchVisible:
                    (outerColor?.id === innerColor?.id && innerConstrColor?.type !== 'white') ||
                    outerConstrColor?.type === 'white',
                bilateralSwitchVisible:
                    outerColor?.id !== innerColor?.id &&
                    (innerConstrColor?.type === 'white' || outerConstrColor?.type === 'white') &&
                    availableColors?.some((c) => c.id === outerColor?.id),
            };
        }
    }

    isWhiteColorButtonDisabled(
        element: ColoredElements,
        side?: ProfileColoredSides | ColoredSides,
        sash?: ColoredSashes,
        alt?: boolean,
        conf = this.configurationsService.conf.Current
    ) {
        if (side === 'inner') {
            if ((element === 'frame' || element === 'sash')) {
                const innerConstrColor = this.newColorsService.getElementConstrColor(
                    conf,
                    element,
                    'inner'
                );
                return innerConstrColor?.type === 'white';
            } else {
                const innerColor = this.newColorsService.getElementColor(conf, {
                    element,
                    sash,
                    alt,
                    side: 'inner',
                });
                return innerColor?.coreColors.some((c) =>
                    innerColor.constructColors[c].some((el) => el.type === 'white')
                ) || false;
            }
        }

        return false;
    }

    openModalColor(colorControl: ColorControl) {
        const triggerElement = document.activeElement as HTMLElement;
        const pageContainer = document.getElementById('page-container');
        const scrollTop = pageContainer?.scrollTop || 0;
        this.ngZone.run(() => {
            this.newColorsService.openModalColorPicker(
                this.configurationsService.conf.Current,
                colorControl,
                colorControl.title
            ).then(() => {
                if (pageContainer) {
                    pageContainer.scrollTop = scrollTop;
                }
                requestAnimationFrame(() => {
                    if (pageContainer) {
                        pageContainer.scrollTop = scrollTop;
                    }
                    triggerElement?.focus({ preventScroll: true });
                });
            })
        });
    }

    setBilateralColor(colorControl: ColorControl) {
        this.ngZone.run(() => {
            if (colorControl.side === 'inner') {
                this.newColorsService.setBilateralColor(
                    this.configurationsService.conf.Current,
                    colorControl
                );
            }
        });
    }

    setWhiteColor(colorControl: ColorControl) {
        this.ngZone.run(() => {
            this.newColorsService.setWhiteColor(this.configurationsService.conf.Current, colorControl);
        });
    }

    availableColorsSashExt() {
        const hasInnerFillingColor =  this.configurationsService.conf.Current.Sashes.some(sash => this.hasInnerFillingColor(this.configurationsService.conf.Current, sash));
        const hasFrameInnerColorSelect = this.hasFrameInnerColorSelect(this.configurationsService.conf.Current, true);
        return (
            (this.configurationsService.conf.Current.System.different_sash_color &&
                this.config().IccConfig.Configurators.selectSashFrameColor &&
                !this.configurationsService.conf.Current.System.door_type
                 && this.hasSash()
                ) ||
            (this.configurationsService.conf.Current.type === 'door' &&
                this.configurationsService.conf.Current.System.door_type &&
                (hasInnerFillingColor || hasFrameInnerColorSelect))
        );
    }

    hasCoreColorSelect(
        conf = this.configurationsService.conf.Current,
        controlDef: ColorControlDef
    ) {
        if (conf.System?.type !== 'pvc') {
            return false;
        }
        if (conf.System?.door_type) {
            return false;
        }
        const outerColor = this.newColorsService.getElementColor(conf, {
            element: controlDef.element,
            side: 'outer',
            sash: controlDef.sash,
            alt: controlDef.alt,
        });

        const innerColor = this.newColorsService.getElementColor(conf, {
            element: controlDef.element,
            side: 'inner',
            sash: controlDef.sash,
            alt: controlDef.alt,
        });

        return !outerColor?.isCore && !innerColor?.isCore;
    }

    hasFillingElement(conf = this.configurationsService.conf.Current) {
        return conf.Sashes.some(
            (s) =>
                s.glazing?.type &&
                ['door_panels', 'deco_panels', 'pvc_panels'].includes(s.glazing.type)
        );
    }

    hasDecoFillingElement(conf = this.configurationsService.conf.Current) {
        return conf.Sashes.some(
            (s) =>
                s.glazing?.type &&
                ['door_panels', 'deco_panels'].includes(s.glazing.type)
        );
    }

    hasDoorActiveSash(conf = this.configurationsService.conf.Current) {
        return conf.Sashes.some(
            (s) =>
                (s.type?.type === 'DRA' || s.type?.type === 'DOA') &&
                s.glazing?.type &&
                ['door_panels', 'deco_panels', 'pvc_panels'].includes(s.glazing.type)
        );
    }

    hasDoorPassiveSash(conf = this.configurationsService.conf.Current) {
        return conf.Sashes.some(
            (s) =>
                (s.type?.type === 'DRP' || s.type?.type === 'DOP') &&
                s.glazing?.type &&
                ['door_panels', 'deco_panels', 'pvc_panels'].includes(s.glazing.type)
        );
    }

    hasWindowSashWithPanel(conf = this.configurationsService.conf.Current) {
        return conf.Sashes.some(
            (s) =>
                s.type?.type !== 'DRA' &&
                s.type?.type !== 'DRP' &&
                s.type?.type !== 'DOA' &&
                s.type?.type !== 'DOP' &&
                s.glazing?.type &&
                ['door_panels', 'deco_panels', 'pvc_panels'].includes(s.glazing.type)
        );
    }

    hasInnerFillingColor(conf = this.configurationsService.conf.Current, sash: ActiveSash) {
        return (
            !this.newColorsService.isDoubleSidedColorAvailable(conf) ||
            (conf.System.door_type && sash?.panelType === 'Mixed')
        );
    }

    hasAltFillingColor(
        conf = this.configurationsService.conf.Current,
        sash: ActiveSash,
        side: ColoredSides | ProfileColoredSides
    ) {
        if (side === 'inner') {
            return (
                sash.panelInner?.type === 'door_panels' &&
                sash.panelInner.available_second_inside_color
            );
        }
        return (
            sash.glazing &&
            sash.glazing.type === (conf.System.door_type ? 'door_panels' : 'deco_panels') &&
            sash.glazing.available_second_color
        );
    }

    hasFrameColorSelect(conf = this.configurationsService.conf.Current) {
        if (conf.type !== 'door') {
            return true;
        }
        return !this.hasDecoFillingElement(conf) || conf.System.separate_frame_and_sash_color;
    }

    hasFrameInnerColorSelect(conf = this.configurationsService.conf.Current, skipMode = false) {
        if (conf.type !== 'door') {
            return true;
        }

        const outerColor = this.newColorsService.getElementColor(conf, {
            element: 'frame',
            side: 'outer',
        });

        const innerColor = this.newColorsService.getElementColor(conf, {
            element: 'frame',
            side: 'inner',
        });


        return (
            !this.hasDecoFillingElement(conf) ||
            (conf.System.separate_frame_and_sash_color &&
                !this.newColorsService.onlyDoubleSidedFrameColor(conf) &&
                (!conf.System.door_type ||
                    (conf.System.door_type &&
                    (this.mode === 'extended' || skipMode || outerColor?.id !== innerColor?.id)))) ||
            conf.HasAlushell
        );
    }

    hasLippingInnerColor(conf = this.configurationsService.conf.Current) {
        if (conf.System.lipping_color_based_on_frame_color) {
            return (
                conf.System.available_lipping_in_bicolor &&
                (!conf.System.door_type || (conf.System.door_type && this.mode === 'extended'))
            );
        }

        if (conf.System.lipping_price_level_id && this.mode === 'extended') {
            return !this.lippingColorsService.isOnlyDoubleSidePriceLevelAvailable();
        }
    }

    hasThresholdColorSelect(conf: WindowActiveConfiguration) {
        const profile = this.profilesService.getProfile(
            this.profilesService.getUsedThresholdId(conf)
        );
        return profile && profile.priceLevelId !== null;
    }

    hasSlat(conf: WindowActiveConfiguration) {
        return conf.Slat;
    }

    hasSash(conf = this.configurationsService.conf.Current) {
        return conf.Sashes.length > 0 && conf.Sashes.some(s => s.type?.type !== 'F');
    }
}
